import { getAccessToken } from '@/lib/tokenStorage';

import { PaginatedResult, PaginationResult } from '../types';
import {
  AddEmbryosToShipmentInput,
  AddEmbryosToShipmentInputV2,
  AddImplantedEmbryosV2Input,
  AddOutsideEmbryoInput,
  AddStrawsV2Input,
  AnnotationDto,
  ClientEmbryoResponse,
  CreateClientEmbryosInput,
  EditEmbryoInput,
  EditOutsideEmbryoInput,
  EmbryoApiResponse,
  EmbryoCalfDto,
  EmbryoDetailDto,
  EmbryoPayload,
  EmbryoShipmentPayload,
  FreezeEntryDto,
  FreezeEntryInput,
  ImplantedEmbryoDto,
  ImplantedEmbryoInput,
  OutsideEmbryoApiResponse,
  OutsideEmbryoPayload,
  ShipmentPayload,
} from './types';

const fetchOutsideEmbryos = async (
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<OutsideEmbryoApiResponse> => {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const url = `${API_BASE_URL}/api/embryo/outside${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

const fetchEmbryoById = async (
  id: string,
  isOutsideEmbryo: boolean = false,
): Promise<EmbryoDetailDto | null> => {
  const token = getAccessToken();
  if (!token) throw new Error('No access token available. Please log in.');

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const url = `${API_BASE_URL}/api/embryo/${id}?isOutsideEmbryo=${isOutsideEmbryo}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as EmbryoDetailDto;
};

async function editEmbryos(
  inputs: EditEmbryoInput[],
): Promise<EmbryoPayload[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while editing embryo', {
      cause: error,
    });
  }
}

async function editOutsideEmbryos(
  inputs: EditOutsideEmbryoInput[],
): Promise<EmbryoPayload[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/outside`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while editing outside embryo', {
      cause: error,
    });
  }
}

async function addEmbryosToShipment(
  input: AddEmbryosToShipmentInput,
): Promise<ShipmentPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/add-embryos-to-shipment`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while adding embryos to shipment', {
      cause: error,
    });
  }
}

async function addEmbryosToShipmentV2(
  input: AddEmbryosToShipmentInputV2,
): Promise<EmbryoShipmentPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/v2/embryo/add-embryos-to-shipment`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while adding embryos to shipment', {
      cause: error,
    });
  }
}

async function createAnnotation(
  embryoId: string,
  noteText: string,
): Promise<void> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/${embryoId}/annotation`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ objectId: embryoId, noteText }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}

async function fetchAnnotations(embryoId: string): Promise<AnnotationDto[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/${embryoId}/annotation`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

async function fetchEmbryoCalves(
  embryoIds: string[],
): Promise<EmbryoCalfDto[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/calves`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(embryoIds),
  });

  if (!response.ok) {
    if (response.status === 404) return [];
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

async function deleteAnnotation(
  embryoId: string,
  annotationId: string,
): Promise<void> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/${embryoId}/annotation/${annotationId}`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Annotation not found');
    }
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}

async function addOutsideEmbryosV2(
  input: AddOutsideEmbryoInput,
): Promise<OutsideEmbryoPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/v2/outside-embryos`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message);
  }

  return data;
}

async function addOutsideEmbryos(
  input: AddOutsideEmbryoInput,
): Promise<OutsideEmbryoPayload> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/outside-embryos`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message);
  }

  return data;
}

async function addStrawsV2(input: AddStrawsV2Input): Promise<any> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) throw new Error('VITE_API_BASE_URL is not defined');
  const url = `${API_BASE_URL}/api/v2/embryo/add-straws`;

  const token = getAccessToken();
  if (!token) throw new Error('No access token available. Please log in.');

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch'))
      throw new Error(`Network error: Unable to connect to ${url}.`, {
        cause: error,
      });
    if (error instanceof Error) throw error;
    throw new Error('Unknown error occurred while adding straws', {
      cause: error,
    });
  }
}

async function addImplantedEmbryosV2(
  input: AddImplantedEmbryosV2Input,
): Promise<any> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) throw new Error('VITE_API_BASE_URL is not defined');
  const url = `${API_BASE_URL}/api/v2/embryo/add-implanted-embryos`;

  const token = getAccessToken();
  if (!token) throw new Error('No access token available. Please log in.');

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch'))
      throw new Error(`Network error: Unable to connect to ${url}.`, {
        cause: error,
      });
    if (error instanceof Error) throw error;
    throw new Error('Unknown error occurred while adding implanted embryos', {
      cause: error,
    });
  }
}

const fetchFreezeEntries = async (
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<PaginationResult<FreezeEntryDto>> => {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const url = `${API_BASE_URL}/api/embryo/freeze-entries${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

const fetchImplantedEmbryos = async (
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<PaginationResult<ImplantedEmbryoDto>> => {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const url = `${API_BASE_URL}/api/embryo/implanted${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

async function editImplantedEmbryos(
  inputs: ImplantedEmbryoInput[],
): Promise<unknown> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/implanted-embryos`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) throw error;
    throw new Error('Unknown error occurred while editing implanted embryos', {
      cause: error,
    });
  }
}

async function editFreezeEntries(inputs: FreezeEntryInput[]): Promise<unknown> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/embryo/freeze-entries`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please ensure the API server is running.`,
        { cause: error },
      );
    }
    if (error instanceof Error) throw error;
    throw new Error('Unknown error occurred while editing freeze entries', {
      cause: error,
    });
  }
}

async function createClientEmbryos(
  input: CreateClientEmbryosInput,
): Promise<ClientEmbryoResponse[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) throw new Error('VITE_API_BASE_URL is not defined');
  const url = `${API_BASE_URL}/api/embryo/client-embryos`;

  const token = getAccessToken();
  if (!token) throw new Error('No access token available. Please log in.');

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch'))
      throw new Error(`Network error: Unable to connect to ${url}.`, {
        cause: error,
      });
    if (error instanceof Error) throw error;
    throw new Error('Unknown error occurred while creating client embryos', {
      cause: error,
    });
  }
}
async function fetchEmbryosFromIndex(
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<{
  items: EmbryoApiResponse[];
  totalCount: number;
  queryMs: number | null;
}> {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }

  const url = `${API_BASE_URL}/api/v2/embryo/index${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const header = response.headers.get('X-Query-Ms');
  const data = await response.json();
  return { ...data, queryMs: header ? Number(header) : null };
}

export const embryoServiceApi = {
  getEmbryosFromIndex: fetchEmbryosFromIndex,
  editEmbryos,
  editOutsideEmbryos,
  editImplantedEmbryos,
  editFreezeEntries,
  getFreezeEntries: fetchFreezeEntries,
  getImplantedEmbryos: fetchImplantedEmbryos,
  getOutsideEmbryos: fetchOutsideEmbryos,
  addOutsideEmbryos,
  addOutsideEmbryosV2,
  getEmbryoById: fetchEmbryoById,
  addEmbryosToShipment,
  addEmbryosToShipmentV2,
  createAnnotation,
  addStrawsV2,
  addImplantedEmbryosV2,
  getAnnotations: fetchAnnotations,
  deleteAnnotation,
  getEmbryoCalves: fetchEmbryoCalves,
  createClientEmbryos,
};
